import clsx from "clsx";
import { FC, Suspense } from "react";
import { networkLabel, priceFormatter } from "rujira.js";
import {
  AssetLabel,
  Button,
  Decimal,
  Fiat,
  IconDenom,
  Icons,
  NetworkIcon,
  nFormatter,
  Popout,
  useGlobalModalContext,
  useLocalStorage,
  Warning,
} from "rujira.ui";
import exclamation from "rujira.ui/src/assets/images/exclamation.gif";
import { ExclamationTriangle } from "rujira.ui/src/components/icons/Icons";
import Anim from "../../common/assets/cosmos.svg";
import Animation from "../../common/assets/portfolio.gif";
import { BalanceContextFragment$data } from "../../common/components/__generated__/BalanceContextFragment.graphql";
import {
  BalanceProvider,
  usePreloadedBalance,
  usePreloadedBalanceData,
} from "../../common/components/Balance";
import { useDeposit } from "../../common/components/Deposit";
import { Link } from "../../Gate";
import { useAccounts } from "../../services/accounts";
import { Success } from "../../swap/components/Quote";
import { usePortfolio } from "../Portfolio";
import { Send } from "./actions/Send";
import { Withdraw } from "./actions/Withdraw";
const { Chart, Merge, MoneyTransfer, Swap, TrendDown, TrendUp } = Icons;

type Success = Extract<
  BalanceContextFragment$data["balancesV2"],
  { ok: true }
>["value"];

type Failure = Extract<
  BalanceContextFragment$data["balancesV2"],
  { ok: false }
>["errors"];

export const Balances: FC = () => {
  const data = usePreloadedBalanceData();

  return data ? (
    data.ok ? (
      data.value.edges?.length === 0 ? (
        <div className="relative card p-3 mt-2">
          <Empty />
        </div>
      ) : (
        <List data={data.value} />
      )
    ) : (
      <div className="relative card p-3 mt-2">
        <Failure errors={data.errors} />
      </div>
    )
  ) : (
    <Loading />
  );
};

const List: FC<{
  data: Success;
}> = ({ data }) => {
  const { hideSmall } = usePortfolio();

  const isLowRune = data.edges?.some(
    (x) =>
      x?.node &&
      x?.node.asset.metadata.symbol === "RUNE" &&
      x.node.balance < 2000000n
  );

  const filtered =
    (hideSmall
      ? data.edges?.filter((x) => x?.node && x.node.valueUsd > 100000000n)
      : data.edges) || [];

  return filtered.length ? (
    <>
      <div className="relative card p-3 mt-2">
        {isLowRune && <LowRuneNotice network="GAIA" symbol="ATOM" />}
        <div className="table-sticky table-sticky--first">
          <table className="table table--big condensed portfolio__balances">
            <thead>
              <tr>
                <th className="w-1 bg-black"></th>
                <th style={{ paddingLeft: 0 }}>Token</th>
                <th>Networks</th>
                <th className="text-right">Balance</th>
                <th className="text-right">Price</th>
                <th className="text-right">24h Change</th>
                <th className="text-right">Value</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              <BalanceProvider
                asset={{
                  type: "LAYER_1",
                  chain: "BTC",
                  asset: "BTC.BTC",
                  metadata: {
                    symbol: "BTC",
                    decimals: 8,
                  },
                }}>
                <KeplrBtcWarning />
              </BalanceProvider>
              {[...filtered]
                .sort((a, b) =>
                  Number((b?.node?.valueUsd || 0n) - (a?.node?.valueUsd || 0n))
                )
                .map((x) => {
                  if (!x?.node) return null;
                  return (
                    <BalanceProvider asset={x.node.asset}>
                      <BalanceItem key={x.node.id} />
                    </BalanceProvider>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  ) : (
    <div className="relative card p-3 mt-2">
      <Empty isLowRune={isLowRune} />
    </div>
  );
};

const Failure: FC<{ errors: Failure }> = ({ errors }) => {
  return (
    <>
      {errors.map((e, idx) =>
        e &&
        typeof e === "object" &&
        "status" in e &&
        typeof e.status === "string" ? (
          <Warning key={idx} color="red" className="condensed mt-2">
            <ExclamationTriangle className="color-red" />
            <span className="warning__msg">{e.status}</span>
          </Warning>
        ) : null
      )}
    </>
  );
};

const Empty: FC<{ isLowRune?: boolean }> = ({ isLowRune = true }) => {
  return (
    <>
      {isLowRune && <LowRuneNotice network="GAIA" symbol="ATOM" />}
      <img
        src={Animation}
        alt="No balances"
        className="block w-10 h-10 mx-a mb-1 filter-grey"
      />
      <p className="color-grey fw-500 text-center">No available balances</p>
      <p className="flex jc-c ai-c gap-x-1 mt-2 color-grey">
        <Button
          onClick={() => document.getElementById("deposit")?.click()}
          className="button--sm button--icon-right button--outline"
          label="Deposit Now">
          <Icons.ArrowUpRight />
        </Button>
      </p>
    </>
  );
};

const KeplrBtcWarning = () => {
  const [infoMsgDismissals, setInfoMsgDismissals] = useLocalStorage<string[]>(
    "keplr-btc-info-dismissals",
    []
  );

  const { selected } = useAccounts();
  const { balance } = usePreloadedBalance();
  if (!!balance?.balance) return null;
  if (selected?.provider !== "Keplr") return null;
  if (infoMsgDismissals?.some((s) => s === selected.address)) return null;

  return (
    <tr>
      <td colSpan={8}>
        <div className="flex jc-c pt-1.5">
          <Warning
            className="warning--sm mb-2 condensed flex jc-c"
            color="orange">
            <div className="flex ai-c">
              <img
                src={exclamation}
                className="filter-orange block no-shrink"
                style={{ width: "1.5rem", height: "1.5rem" }}
              />
              <p className="text-left ml-1 lh-19 fs-14">
                Missing BTC balance? Toggle between Taproot and SegWit addresses
                for Bitcoin in the Keplr extension
              </p>
            </div>
            <Button
              className={"button--orange button--outline button--xs ml-2"}
              onClick={() => {
                if (selected?.address) {
                  setInfoMsgDismissals([
                    ...infoMsgDismissals,
                    selected.address,
                  ]);
                }
              }}
              label="Dismiss"
            />
          </Warning>
        </div>
      </td>
    </tr>
  );
};

export const LowRuneNotice: FC<{
  network: string;
  symbol: string;
  msg?: string;
}> = ({ network, symbol, msg }) => {
  return (
    <Link to={`/swap/${network}.${symbol}/THOR.RUNE`} className="mb-3 block">
      <Warning
        msg={
          msg ||
          `You don't have enough RUNE for gas. Swap tokens for RUNE here.`
        }
        className="fs-12 lh-18 fw-600 color-white warning--pre hover-teal">
        <Icons.NoGas className="color-teal w-1 h-1" />
      </Warning>
    </Link>
  );
};

const Loading: FC = () => {
  return (
    <div className="mt-4 text-center">
      <img src={Anim} alt="" className="block w-12 h-12 mx-a mb-2" />
    </div>
  );
};

const BalanceItem: FC = () => {
  const { balance, asset } = usePreloadedBalance();

  const sorted = [...(balance?.accounts || [])].sort((a, b) => {
    if (a.asset.chain === "THOR" || a.asset.type === "SECURED") return -1;
    if (b.asset.chain === "THOR" || b.asset.type === "SECURED") return 1;
    return networkLabel(a.asset.chain).localeCompare(
      networkLabel(b.asset.chain)
    );
  });

  const deposit = useDeposit(0n, balance?.asset);

  if (!balance) return null;
  const price = asset.price;

  return (
    <tr>
      <th className="bg-black">
        <IconDenom denom={balance.asset.metadata.symbol} />
      </th>
      <td style={{ paddingLeft: 0 }} className="portfolio__token">
        <div className="flex ai-c no-shrink">
          <h3 className="flex ai-b">
            <AssetLabel
              asset={balance.asset}
              Container={({ children }) => (
                <span className="color-grey fs-14 fs-lg-16">{children}</span>
              )}
            />
          </h3>
          {isMerge(balance.asset.metadata.symbol) && (
            <Link
              to={`/merge/${balance.asset.metadata.symbol}`}
              className="tag tag--md tag--teal ml-1">
              merge...
            </Link>
          )}
          {isSwitch(balance.asset.metadata.symbol) && (
            <button onClick={deposit} className="tag tag--md tag--orange ml-1">
              migrate...
            </button>
          )}
        </div>
      </td>
      <td>
        <div className="nowrap flex gap-0.5">
          {sorted.map((x) => {
            return (
              <div
                key={x.asset.asset}
                data-tooltip-content={`${nFormatter(x.balance, 8, 8)} on ${networkLabel(x.asset)}`}
                data-tooltip-id="global-tip">
                <NetworkIcon
                  className="w-2.5 h-2.5"
                  network={x.asset.type === "SECURED" ? "THOR" : x.asset.chain}
                />
              </div>
            );
          })}
        </div>
      </td>
      <td className="text-right">
        <Decimal amount={balance.balance} round={6} />
      </td>
      <td className="text-right">
        {price?.current ? <>${priceFormatter(price.current)}</> : null}
      </td>
      <td className="text-right">
        <div
          className={clsx({
            "flex ai-c jc-e": true,
            "color-teal": price?.changeDay && price.changeDay > 0,
            "color-red": price?.changeDay && price.changeDay < 0,
          })}>
          {price?.changeDay ? (
            <>
              {price.changeDay.toLocaleDecimal(2)}%
              {price.changeDay > 0 ? (
                <TrendUp className="h-2 w-a ml-0.5" />
              ) : (
                <TrendDown className="h-2 w-a ml-0.5" />
              )}
            </>
          ) : null}
        </div>
      </td>

      <td className="text-right">
        <Fiat
          amount={balance.valueUsd}
          decimals={8}
          symbol="$"
          padSymbol={false}
        />
      </td>
      <td className="text-right">
        <Popout buttonClassName="button--outline button--grey mr-0.5">
          <Suspense>
            <TokenActions />
          </Suspense>
        </Popout>
      </td>
    </tr>
  );
};

const TokenActions: FC = () => {
  const { balance } = usePreloadedBalance();
  const { showModal, hideModal } = useGlobalModalContext();

  const withdraw = () => {
    showModal({
      title: `Withdraw ${balance?.asset.metadata.symbol}`,
      backgroundClose: false,
      children: balance ? (
        <BalanceProvider asset={balance.asset}>
          <Withdraw cancel={hideModal} />
        </BalanceProvider>
      ) : (
        <Withdraw cancel={hideModal} />
      ),
    });
  };

  const deposit = useDeposit(0n, balance?.asset);

  const send = () => {
    if (!balance) return;
    showModal({
      backgroundClose: false,
      children: (
        <BalanceProvider asset={balance?.asset}>
          <Send cancel={hideModal} />
        </BalanceProvider>
      ),
    });
  };

  if (!balance) return null;

  const asset = balance.asset.metadata.symbol;

  const canWithdraw = !!balance.accounts.find(
    (a) => a.asset.type === "SECURED"
  );
  const canDeposit = !!balance.accounts.find((a) => a.asset.type === "LAYER_1");

  return (
    <nav>
      {isMerge(balance.asset.metadata.symbol) && (
        <Link to={`/merge/${balance.asset.metadata.symbol}`}>
          <Merge />
          Merge
        </Link>
      )}
      {canDeposit && (
        <button onClick={deposit}>
          <Icons.Deposit />
          Deposit
        </button>
      )}

      {canWithdraw && (
        <button onClick={withdraw}>
          <Icons.Withdraw />
          Withdraw
        </button>
      )}

      <button onClick={send}>
        <MoneyTransfer />
        Send
      </button>
      <Link to={asset === "BTC" ? `/swap/${asset}/ETH` : `/swap/${asset}/BTC`}>
        <Swap />
        Swap
      </Link>
      <Link to={`/trade?q=${balance.asset.metadata.symbol}`}>
        <Chart />
        Trade
      </Link>
    </nav>
  );
};
const isMerge = (symbol: string) => {
  return [
    "KUJI",
    "rKUJI",
    "RKUJI",
    "FUZN",
    "WINK",
    "NSTK",
    "LVN",
    "NAMI",
  ].includes(symbol);
};

const isSwitch = (symbol: string) => {
  return ["AUTO", "LQDY", "MNTA"].includes(symbol);
};
